import { ChevronRightIcon } from '@heroicons/react/24/outline';
import type { Solicitacao } from '@/features/SI/models';
import { TIPO_SOLICITACAO_LABELS } from '@/features/SI/pages/NovaSolicitacaoPage/novaSolicitacaoPage.definitions';
import {
  STATUS_SOLICITACAO_BADGE_CLASS,
  STATUS_SOLICITACAO_LABELS,
} from '@/features/SI/pages/SolicitacoesListPage/solicitacoesListPage.definitions';
import { dataAberturaMillis } from '../siSolicitacoesPage.definitions';

interface SiSolicitacoesListaProps {
  /** Já filtradas e ordenadas (ver filtrarSolicitacoes). */
  itens: Solicitacao[];
  isLoading: boolean;
  nomeCliente: (clienteId: string | null | undefined) => string | undefined;
  vazio: string;
  /** Nome de quem abriu, quando não foi a própria pessoa (time RH) — null esconde. */
  abertaPor?: (solicitacao: Solicitacao) => string | null;
  onSelect: (id: string) => void;
}

/** Lista simples (padrão "Meus pedidos" do Portal do DP). O colaborador aparece sempre: quem
 * abre pode abrir pra outra pessoa (RH, gestor). */
export function SiSolicitacoesLista({ itens, isLoading, nomeCliente, vazio, abertaPor, onSelect }: SiSolicitacoesListaProps) {
  if (isLoading) {
    return <div className="h-16 animate-pulse rounded-2xl bg-raro-gray-100" />;
  }

  if (itens.length === 0) {
    return (
      <p className="rounded-2xl border border-dashed border-raro-gray-300 bg-white px-4 py-6 text-center text-sm text-raro-gray-500">
        {vazio}
      </p>
    );
  }

  return (
    <ul className="divide-y divide-raro-gray-100 overflow-hidden rounded-2xl border border-raro-gray-200 bg-white shadow-sm">
      {itens.map((solicitacao) => {
        const clienteNome = nomeCliente(solicitacao.clienteId);
        const abertaEm = dataAberturaMillis(solicitacao);
        const autor = abertaPor?.(solicitacao) ?? null;
        return (
          <li key={solicitacao.id}>
            <button
              type="button"
              onClick={() => onSelect(solicitacao.id)}
              className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition-colors hover:bg-primary-50/60 focus-visible:bg-primary-50/60 focus-visible:outline-none"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-raro-gray-900">
                  {TIPO_SOLICITACAO_LABELS[solicitacao.tipoSolicitacao]}
                  {solicitacao.colaboradorNome && ` · ${solicitacao.colaboradorNome}`}
                  {clienteNome && ` · ${clienteNome}`}
                </p>
                <p className="text-xs text-raro-gray-500">
                  {abertaEm ? `Aberta em ${new Date(abertaEm).toLocaleDateString('pt-BR')}` : 'Solicitação'}
                  {autor && ` · por ${autor}`}
                </p>
              </div>
              <span className="flex shrink-0 items-center gap-3">
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_SOLICITACAO_BADGE_CLASS[solicitacao.status]}`}
                >
                  {STATUS_SOLICITACAO_LABELS[solicitacao.status]}
                </span>
                <ChevronRightIcon className="h-4 w-4 text-raro-gray-400" aria-hidden="true" />
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
